import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface BenchmarkMetric {
  title: string;
  yourValue: number | string;
  industryAverage: number | string;
  topPerformer: number | string;
  unit?: string;
  isPercentage?: boolean;
  trend: 'up' | 'down' | 'neutral';
  percentile: number;
}

const benchmarkData: BenchmarkMetric[] = [
  {
    title: 'Total Brands',
    yourValue: 8,
    industryAverage: 12,
    topPerformer: 25,
    trend: 'down',
    percentile: 35
  },
  {
    title: 'Total Products',
    yourValue: 324,
    industryAverage: 280,
    topPerformer: 450,
    trend: 'up',
    percentile: 65
  },
  {
    title: 'Certified Products',
    yourValue: 156,
    industryAverage: 140,
    topPerformer: 290,
    trend: 'up',
    percentile: 58
  },
  {
    title: 'Certification Rate',
    yourValue: '48.1',
    industryAverage: '42.3',
    topPerformer: '78.5',
    unit: '%',
    isPercentage: true,
    trend: 'up',
    percentile: 62
  },
  {
    title: 'Carbon Reduction',
    yourValue: '18.3',
    industryAverage: '15.7',
    topPerformer: '28.9',
    unit: '%',
    isPercentage: true,
    trend: 'up',
    percentile: 68
  },
  {
    title: 'Safer Ingredients',
    yourValue: '67.3',
    industryAverage: '58.2',
    topPerformer: '84.7',
    unit: '%',
    isPercentage: true,
    trend: 'up',
    percentile: 72
  },
  {
    title: 'Green Score',
    yourValue: '4.2',
    industryAverage: '3.8',
    topPerformer: '4.9',
    unit: '/5',
    trend: 'up',
    percentile: 75
  },
  {
    title: 'Ingredients Reformulated',
    yourValue: 432,
    industryAverage: 385,
    topPerformer: 650,
    trend: 'up',
    percentile: 63
  },
  {
    title: 'CO₂ Saved (Tons)',
    yourValue: 1247,
    industryAverage: 980,
    topPerformer: 1850,
    trend: 'up',
    percentile: 71
  }
];

const chartData = [
  { name: 'Your Value', value: 65, fill: '#3b82f6' },
  { name: 'Industry Average', value: 52, fill: '#94a3b8' },
  { name: 'Top Performer', value: 85, fill: '#22c55e' }
];

const chartConfig = {
  value: {
    label: 'Performance Score',
    color: '#3b82f6'
  }
};

export const BenchmarkComparison = () => {
  const getPerformanceColor = (percentile: number) => {
    if (percentile >= 75) return 'text-green-600';
    if (percentile >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (percentile: number) => {
    if (percentile >= 75) return <Badge variant="default" className="bg-green-100 text-green-800">Top Quartile</Badge>;
    if (percentile >= 50) return <Badge variant="secondary">Above Average</Badge>;
    return <Badge variant="destructive">Below Average</Badge>;
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Overall Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="value" radius={4}>
                    <LabelList dataKey="value" position="top" />
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Performance Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={[
                    { name: 'Top Quartile', value: 25, fill: '#22c55e' },
                    { name: 'Above Average', value: 40, fill: '#3b82f6' },
                    { name: 'Below Average', value: 25, fill: '#f59e0b' },
                    { name: 'Bottom Quartile', value: 10, fill: '#ef4444' }
                  ]} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="value" radius={4}>
                    <LabelList dataKey="value" position="top" />
                    {[
                      { fill: '#22c55e' },
                      { fill: '#3b82f6' },
                      { fill: '#f59e0b' },
                      { fill: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Individual Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {benchmarkData.map((metric, index) => (
          <Card key={index}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
                {getTrendIcon(metric.trend)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">
                    {metric.yourValue}{metric.unit}
                  </span>
                  {getPerformanceBadge(metric.percentile)}
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Your Performance</span>
                    <span className={getPerformanceColor(metric.percentile)}>
                      {metric.percentile}th percentile
                    </span>
                  </div>
                  <Progress value={metric.percentile} className="h-2" />
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block">Industry Avg</span>
                    <span className="font-medium">
                      {metric.industryAverage}{metric.unit}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Top Performer</span>
                    <span className="font-medium">
                      {metric.topPerformer}{metric.unit}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
